import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

// Layouts
import { MainLayout } from '@/components/layout/MainLayout';

// Auth pages
import { Login } from '@/pages/auth/Login';
import { Register } from '@/pages/auth/Register';
import { ForgotPassword } from '@/pages/auth/ForgotPassword';

// Dashboard pages
import { Dashboard } from '@/pages/dashboard/Dashboard';

// Leads pages
import { LeadList, AddLead, LeadDetail } from '@/pages/leads';

// Visits pages
import { VisitList, VisitDetail, ScheduleVisit } from '@/pages/visits';

// Wallet pages
import { WalletDashboard, TopupRequest } from '@/pages/wallet';

// Billing pages
import { InvoiceList, InvoiceDetail, CreateInvoice } from '@/pages/billing';

// Referrals pages
import { ReferralList, ReferralDetail, SendReferral } from '@/pages/referrals';

// Properties pages
import { PropertyList, PropertyDetail } from '@/pages/properties';

// Reports pages
import { Reports } from '@/pages/reports';

// Settings pages
import { Settings } from '@/pages/settings';

// Error pages
import { NotFound } from '@/pages/NotFound';

/**
 * Protected Route wrapper component
 * Redirects to login if user is not authenticated
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-secondary-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/**
 * Public Route wrapper component
 * Redirects to dashboard if user is already authenticated
 */
interface PublicRouteProps {
  children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-secondary-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />

      {/* Protected routes with MainLayout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />

        {/* Leads routes */}
        <Route path="leads" element={<LeadList />} />
        <Route path="leads/new" element={<AddLead />} />
        <Route path="leads/:id" element={<LeadDetail />} />

        {/* Visits routes */}
        <Route path="visits" element={<VisitList />} />
        <Route path="visits/schedule" element={<ScheduleVisit />} />
        <Route path="visits/:id" element={<VisitDetail />} />

        {/* Wallet routes */}
        <Route path="wallet" element={<WalletDashboard />} />
        <Route path="wallet/topup" element={<TopupRequest />} />

        {/* Billing routes */}
        <Route path="billing" element={<InvoiceList />} />
        <Route path="billing/invoices/new" element={<CreateInvoice />} />
        <Route path="billing/invoices/:id" element={<InvoiceDetail />} />

        {/* Referrals routes */}
        <Route path="referrals" element={<ReferralList />} />
        <Route path="referrals/send" element={<SendReferral />} />
        <Route path="referrals/:id" element={<ReferralDetail />} />

        {/* Properties routes */}
        <Route path="properties" element={<PropertyList />} />
        <Route path="properties/:id" element={<PropertyDetail />} />

        {/* Reports route */}
        <Route path="reports" element={<Reports />} />

        {/* Settings route */}
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* 404 Not Found */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default App;
